import React, { useState } from 'react';
import { PaymentWithDetails } from '../../types/payment.ts';
import Receipt from './Receipt.tsx';
import { receiptService } from '../../services/receiptService.ts';
import { formatMonthYear, formatCurrency, formatDate } from '../../utils/formatters.ts';
import { useNavigate } from 'react-router-dom';

interface PaymentListProps {
  payments: PaymentWithDetails[];
  onDelete: (id: string) => void;
}

const sortPayments = (payments: PaymentWithDetails[]) => {
  return [...payments].sort((a, b) => {
    // Convert strings to Date objects for proper comparison
    const dateA = new Date(a.payment_date + (a.created_at ? `T${a.created_at.split('T')[1]}` : ''));
    const dateB = new Date(b.payment_date + (b.created_at ? `T${b.created_at.split('T')[1]}` : ''));
    return dateB.getTime() - dateA.getTime(); // Most recent first
  });
};

const truncateText = (text: string, maxLength: number = 25) => {
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

const PaymentList: React.FC<PaymentListProps> = ({ payments, onDelete }) => {
  const navigate = useNavigate();
  const [selectedPayment, setSelectedPayment] = useState<PaymentWithDetails | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const handlePrintReceipt = () => {
    if (selectedPayment) {
      receiptService.printReceipt(selectedPayment);
    }
  };

  const handleRowClick = (e: React.MouseEvent, paymentId: string) => {
    // Prevent navigation if clicking on buttons/actions
    if ((e.target as HTMLElement).closest('.payment-actions')) {
      return;
    }
    navigate(`/payments/${paymentId}`);
  };

  const sortedPayments = sortPayments(payments);
  
  const paginatedPayments = sortedPayments.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const totalPages = Math.ceil(sortedPayments.length / ITEMS_PER_PAGE);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <>
      <div className="payment-list table-responsive">
        {/* Desktop View */}
        <table className="payment-table desktop-only">
          <thead>
            <tr>
              <th>Month</th>
              <th>Tenant</th>
              <th>Property</th>
              <th>Amount</th>
              <th>Mode</th>
              <th>Collected&nbsp;By</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedPayments.map((payment) => (
              <tr 
                key={payment.id}
                onClick={(e) => handleRowClick(e, payment.id)}
                className="payment-row"
              >
                <td className="date-cell">{formatDate(payment.payment_date)}</td>
                <td 
                  className="name-cell" 
                  title={payment.tenant.full_name}
                  aria-label={payment.tenant.full_name}
                >
                  {truncateText(payment.tenant.full_name)}
                </td>
                <td 
                  className="name-cell"
                  title={payment.property.name}
                  aria-label={payment.property.name}
                >
                  {truncateText(payment.property.name)}
                </td>
                <td>{formatCurrency(payment.total_amount)}</td>
                <td>{payment.payment_mode}</td>
                <td>{payment.payment_to}</td>
                <td className="payment-actions">
                  <button
                    onClick={() => setSelectedPayment(payment)}
                    className="btn-view"
                  >
                    View Receipt
                  </button>
                  
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Mobile View */}
        <div className="payment-cards-container mobile-only">
          {paginatedPayments.map((payment) => (
            <div 
              key={payment.id} 
              className="payment-card"
              onClick={(e) => handleRowClick(e, payment.id)}
            >
              <div className="payment-card-header">
                <span className="payment-card-date">{formatDate(payment.payment_date)}</span>
                <span className="payment-amount">{formatCurrency(payment.total_amount)}</span>
              </div>
              
              <div className="payment-card-body">
                <div className="payment-card-row">
                  <label>Tenant:</label>
                  <span>{payment.tenant.full_name}</span>
                </div>
                <div className="payment-card-row">
                  <label>Property:</label>
                  <span>{payment.property.name}</span>
                </div>
                <div className="payment-card-row">
                  <label>Mode:</label>
                  <span>{payment.payment_mode}</span>
                </div>
                <div className="payment-card-row">
                  <label>Collected By:</label>
                  <span>{payment.payment_to}</span>
                </div>
              </div>

              <div className="payment-card-actions">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPayment(payment);
                  }}
                  className="btn-view"
                >
                  View Receipt
                </button>
              </div>
            </div>
          ))}
        </div>

        {sortedPayments.length > ITEMS_PER_PAGE && (
          <div className="pagination">
            <button 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="btn-secondary"
            >
              Previous
            </button>
            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>
            <button 
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="btn-secondary"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {selectedPayment && (
        <Receipt
          payment={selectedPayment}
          onClose={() => setSelectedPayment(null)}
          onPrint={handlePrintReceipt}
        />
      )}
    </>
  );
};

export default PaymentList;
